import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EnvService } from './environment.service';
import { Observable, of } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';

export interface NearMissListRequest {
  guid: string;
  uid: string;
  status: string;
  tab: number;
  min: number;
  max: number;
  sort: string;
  order: string;
  search_item: string;
  client_id: string | null;    // From signin response (user.client_id), fallback to '1' if null
  audit_type_id: string | null; // From signin response (user.audit_type_id), fallback to '2' for near-miss if null
  store_id: string | null;
  tag_store_id: string | null;
  start_date: string;
  end_date: string;
  submitted_by: string;
  refnos: string;
  autofail: boolean;
  severity_id?: string | null;
  region?: string | null;
  owner?: string | null;
}

export interface NearMissListResponse {
  success: boolean;
  data: NearMissItem[];
  total: number;
}

export interface NearMissItem {
  id: string;
  title: string | null;
  status: string;
  severity: string;
  audit_date: string;
  store_id: number;
  store_name: string;
  location_region: string | null;
  audit_type_id: number;
  audit_type_name: string;
  owner: string;
  description: string;
  reported_by: string;
  submitted_date: string | null;
  recipients_notified_count: number;
  type_of_near_miss: string | null;
  near_miss_category: string | null;
  witnesses: string | null;
  job_titles_involved: string | null;
  role_name: string | null;
  assigned_to: string | null;
  expected_completion_date: string | null;
  what_happened: string | null;
  potential_outcome: string | null;
  response_steps_to_prevent_recurrence: string | null;
  root_cause: string | null;
  active: boolean;
  task_id: string;
  task_status: string;
  client_id: number;
  photos: number;
  total_count: number;
  guid: string;
  uid: string;
  seq_no: number;
  tabtype: number;
  severity_id: number;
  status_id: number;
  target_user: string | null;
  images: any | null;
  comments: any | null;
}

@Injectable({
  providedIn: 'root'
})
export class NearMissService {
  private baseUrl = this.environment.baseUrl;

  // Cache keys for localStorage
  private readonly SEVERITY_CACHE_KEY = 'nearmiss_severity_cache';
  private readonly STATUS_CACHE_KEY = 'nearmiss_status_cache';
  private readonly LOCATIONS_CACHE_PREFIX = 'nearmiss_locations_cache_';
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  // In-memory cache for API responses (lost on page refresh)
  private severityListCache$: Observable<any> | null = null;
  private statusListCache$: Observable<any> | null = null;
  private locationsCache: Map<string, Observable<any>> = new Map();

  constructor(
    private http: HttpClient,
    private environment: EnvService
  ) { }

  /**
   * Check if cached data is still valid
   */
  private isCacheValid(cacheKey: string): boolean {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return false;

    try {
      const { timestamp } = JSON.parse(cached);
      const now = new Date().getTime();
      return (now - timestamp) < this.CACHE_DURATION;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get cached data from localStorage
   */
  private getCachedData(cacheKey: string): any {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    try {
      const { data } = JSON.parse(cached);
      return data;
    } catch (e) {
      return null;
    }
  }

  /**
   * Save data to localStorage with timestamp
   */
  private setCachedData(cacheKey: string, data: any): void {
    try {
      const cacheData = {
        data: data,
        timestamp: new Date().getTime()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (e) {
      console.error('Error saving to cache:', e);
    }
  }

  /**
   * Clear all cached data (severity, status, locations)
   * Call this when user logs out or when you need to force refresh
   */
  clearCache(): void {
    // Clear localStorage cache
    localStorage.removeItem(this.SEVERITY_CACHE_KEY);
    localStorage.removeItem(this.STATUS_CACHE_KEY);
    
    // Clear locations cache (remove all items with the prefix)
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.LOCATIONS_CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear in-memory cache
    this.severityListCache$ = null;
    this.statusListCache$ = null;
    this.locationsCache.clear();
  }

  /**
   * Clear only locations cache
   * Call this when locations data might have been updated (e.g., after notification recipients update)
   */
  clearLocationsCache(): void {
    // Clear locations cache from localStorage
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.LOCATIONS_CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear in-memory locations cache
    this.locationsCache.clear();
  }

  /**
   * Get list of near misses
   * @param request Near miss list request payload
   * @returns Observable of near miss list response
   */
  getNearMissList(request: NearMissListRequest): Observable<NearMissListResponse> {
    return this.http.post<NearMissListResponse>(
      `${this.baseUrl}nearmiss/list`,
      request
    );
  }

  /**
   * Create a new near miss
   * @param nearMiss Near miss data
   * @returns Observable of create response
   */
  createNearMiss(nearMiss: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}nearmiss/create`, nearMiss);
  }

  /**
   * Create a new near miss audit
   * @param auditData Audit data with location, user info, etc.
   * @returns Observable of create audit response with seq_no
   */
  createNearMissAudit(auditData: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}nearmiss/create/audit`, auditData);
  }

  /**
   * Update existing near miss
   * @param nearMiss Updated near miss data with required fields
   * @returns Observable of update response
   */
  updateNearMiss(nearMiss: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}nearmiss/update`, nearMiss);
  }

  /**
   * Delete near miss
   * @param id Near miss ID
   * @returns Observable of delete response
   */
  deleteNearMiss(id: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}nearmiss/${id}`);
  }

  /**
   * Export near miss list to CSV
   * @param request Export request payload
   * @returns Observable of export response
   */
  exportNearMissList(request: NearMissListRequest): Observable<Blob> {
    return this.http.post(
      `${this.environment.adminUrl}GoAuditsupload/nearMissReportDownload`,
      request,
      { responseType: 'blob' }
    );
  }

  /**
   * Export near miss as PDF
   * @param request Export request payload
   * @returns Observable of export response
   */
  exportNearMissPdf(request: any): Observable<Blob> {
    return this.http.post(
      `${this.environment.adminUrl}GoAuditsupload/gaupload/`,
      request,
      { responseType: 'blob' }
    );
  }

  /**
   * Generate PDF report for near miss
   * @param payload Report generation payload with audit details
   * @returns Observable of report response with S3 URL
   */
  generatePdfReport(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.environment.adminUrl}reports/api/reports/open/getpdf`,
      payload
    );
  }

  /**
   * Archive a near miss
   * @param payload Archive request payload
   * @returns Observable of archive response
   */
  archiveNearMiss(payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}nearmiss/archive`, payload);
  }

  /**
   * Get locations list (with localStorage and in-memory caching)
   * @param params Location list request params
   * @returns Observable of locations list
   */
  getLocations(params: any): Observable<any> {
    // Create a cache key based on the params
    const cacheKey = this.LOCATIONS_CACHE_PREFIX + JSON.stringify(params);
    
    // Check localStorage first
    if (this.isCacheValid(cacheKey)) {
      const cachedData = this.getCachedData(cacheKey);
      if (cachedData) {
        return of(cachedData);
      }
    }
    
    // Check in-memory cache
    if (!this.locationsCache.has(cacheKey)) {
      // If not cached, make the API call and cache it
      const locations$ = this.http.post<any>(`${this.baseUrl}audits/location/list`, params).pipe(
        tap(data => this.setCachedData(cacheKey, data)),
        shareReplay({ bufferSize: 1, refCount: true })
      );
      this.locationsCache.set(cacheKey, locations$);
    }
    
    return this.locationsCache.get(cacheKey)!;
  }

  /**
   * Get near miss status list (with localStorage and in-memory caching)
   * @returns Observable of status list
   */
  getStatusList(): Observable<any> {
    // Check localStorage first
    if (this.isCacheValid(this.STATUS_CACHE_KEY)) {
      const cachedData = this.getCachedData(this.STATUS_CACHE_KEY);
      if (cachedData) {
        return of(cachedData);
      }
    }

    // Return cached observable if it exists
    if (!this.statusListCache$) {
      // Cache the observable using shareReplay and save to localStorage
      this.statusListCache$ = this.http.get<any>(`${this.baseUrl}nearmiss/status`).pipe(
        tap(data => this.setCachedData(this.STATUS_CACHE_KEY, data)),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.statusListCache$;
  }

  /**
   * Get near miss severity list (with localStorage and in-memory caching)
   * @returns Observable of severity list
   */
  getSeverityList(): Observable<any> {
    // Check localStorage first
    if (this.isCacheValid(this.SEVERITY_CACHE_KEY)) {
      const cachedData = this.getCachedData(this.SEVERITY_CACHE_KEY);
      if (cachedData) {
        return of(cachedData);
      }
    }

    // Return cached observable if it exists
    if (!this.severityListCache$) {
      // Cache the observable using shareReplay and save to localStorage
      this.severityListCache$ = this.http.get<any>(`${this.baseUrl}nearmiss/severity`).pipe(
        tap(data => this.setCachedData(this.SEVERITY_CACHE_KEY, data)),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.severityListCache$;
  }

  /**
   * Get user/owner list for dropdown
   * @param payload Request payload with guid, uid, image_required, and type
   * @returns Observable of user list
   */
  getUserList(payload?: any): Observable<any> {
    const defaultPayload = {
      guid: localStorage.getItem('guid') || '',
      uid: localStorage.getItem('uid') || '',
      image_required: false,
      type: 1
    };
    
    const requestPayload = payload || defaultPayload;
    return this.http.post<any>(`${this.environment.adminUrl}user/api/user/list`, requestPayload);
  }

  /**
   * Get location tag list (regions) for filter dropdown
   * @param payload Request payload with guid, uid, client_id, and active
   * @returns Observable of location tag list
   */
  getLocationTagList(payload?: any): Observable<any> {
    const userJson = localStorage.getItem('currentUser') || localStorage.getItem('user') || '{}';
    const userData = JSON.parse(userJson);
    
    const defaultPayload = {
      guid: userData.guid || '',
      uid: userData.id || userData.uid || '',
      client_id: userData.client_id || '1',
      active: 1
    };
    
    const requestPayload = payload || defaultPayload;
    return this.http.post<any>(`${this.environment.adminUrl}business/api/setup/location/tag/list`, requestPayload);
  }

  /**
   * Get near miss KPI data for stat cards
   * @returns Observable of KPI data
   */
  getNearMissKPI(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}nearmiss/kpi/nearmiss`);
  }

  /**
   * Get location and user counts (active and archive)
   * Returns counts for locations and users with active/archive breakdown
   * @returns Observable of location/user count data
   */
  getLocationCount(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}nearmiss/location/count`);
  }

  /**
   * Get user counts (active and archive)
   * Returns counts for users with active/archive breakdown
   * @returns Observable of user count data
   */
  getUserCount(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}nearmiss/user/count`);
  }

  /**
   * Get list of all near miss IDs for filter dropdown
   * @returns Observable of near miss ID list (array of strings)
   */
  getNearMissIdList(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}nearmiss/idlist`);
  }

  /**
   * Get near miss question/answer data
   * @param payload Request payload with guid, uid, client_id, etc.
   * @returns Observable of question/answer data
   */
  getNearMissData(payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}nearmiss/data`, payload);
  }

  /**
   * Get section list for near miss questionnaire
   * @param params Section list request params
   * @returns Observable of section list
   */
  getSectionList(params: any): Observable<any> {
    const headers = new HttpHeaders({ 'hideLoader': 'true' });
    return this.http.post<any>(`${this.baseUrl}nearmiss/section/list`, params, { headers });
  }

  /**
   * Get questionnaire/question list for near miss
   * @param params Question list request params
   * @returns Observable of question list
   */
  getQuestionList(params: any): Observable<any> {
    const headers = new HttpHeaders({ 'hideLoader': 'true' });
    return this.http.post<any>(`${this.baseUrl}nearmiss/question/list`, params, { headers });
  }

  /**
   * Save question answer for near miss
   * @param answer Question answer data
   * @returns Observable of save response
   */
  saveQuestionAnswer(answer: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}nearmiss/question/save`, answer);
  }
  /**
   * Save photo URL for near miss question
   * @param photoData Photo data payload
   * @returns Observable of save response
   */
  savePhotoUrl(photoData: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}nearmiss/savephotourl`, photoData, { headers: { hideLoader: 'true' } });
  }

  /**
   * Delete photo for near miss question
   * @param request Delete photo request
   * @returns Observable of delete response
   */
  deletePhoto(request: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}nearmiss/delete/photo`, request, { headers: { hideLoader: 'true' } });
  }
  /**
   * Create or update a corrective task for near miss
   * @param taskData Task data including near miss context
   * @returns Observable of task save response
   */
  createTask(taskData: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}nearmiss/task/saveupdate`, taskData);
  }

  /**
   * Save a comment for a near miss
   * @param commentData Comment data including action_id, comments, guid, uid
   * @returns Observable of save comment response
   */
  saveComment(commentData: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}nearmiss/comments/save`, commentData);
  }

  /**
   * Get comments for a near miss
   * @param nearMissId Near miss ID (action_id)
   * @returns Observable of comments list
   */
  getComments(nearMissId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}nearmiss/comments/${nearMissId}`);
  }

  /**
   * Submit near miss report
   * @param payload Report submission payload
   * @returns Observable of submit response
   */
  submitReport(payload: {
    guid: string;
    uid: string;
    client_id: string;
    audit_group_id: string;
    audit_type_id: string;
    store_id: string;
    audit_date: string;
    seq_no: number;
    pLang: string;
    timezone: string;
    end_date: string;
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}nearmiss/report`, payload);
  }
}
